import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { useAtom } from "jotai"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import api from "../../services/api"
import { StatusBadge } from "../../Components/Admin/StatusBadge"
import { InvitationStatusBadge } from "../../Components/Admin/InvitationStatusBadge"
import { ProgressionTimeline } from "../../Components/Admin/ProgressionTimeline"
import { ProgressionCheckModal } from "../../Components/Admin/ProgressionCheckModal"
import { ProgressPlanModal } from "../../Components/Admin/ProgressPlanModal"
import { useDownloadPdf } from "../../hooks/useDownloadPdf"
import Button from "../../Components/Button"
import AdminLayout from "../../Components/Layout/AdminLayout"
import { useUserOrganization } from "../../hooks/useUserOrganization"
import { isSuperAdminAtom, isOrgAdminAtom, isGuideAdminAtom, isGuideAtom } from "../../state"
import FormikField from "../../Components/Forms/FormikField"
import TextField from "../../Components/Forms/FieldTypes/TextField"
import Spinner from "../../Components/Spinner"

const editProfileSchema = Yup.object().shape({
  first_name: Yup.string().required("Required"),
  last_name: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
})

const AdminPlanDetail = () => {
  const { token } = useParams()
  const userOrganization = useUserOrganization()
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [checkModalOpen, setCheckModalOpen] = useState(false)
  const [isSuperAdmin] = useAtom(isSuperAdminAtom)
  const [isOrgAdmin] = useAtom(isOrgAdminAtom)
  const [isGuideAdmin] = useAtom(isGuideAdminAtom)
  const [isGuide] = useAtom(isGuideAtom)
  const [editingUser, setEditingUser] = useState(null)
  const [editError, setEditError] = useState(null)
  const [progressModalOpen, setProgressModalOpen] = useState(false)
  const [checkData, setCheckData] = useState(null)
  const [isProgressing, setIsProgressing] = useState(false)
  const { downloadPdf } = useDownloadPdf()

  const loadPlan = () => {
    setLoading(true)
    api.getAdminPlan(token)
      .then((response) => {
        setPlan(response.data)
        setLoading(false)
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Failed to load plan")
        setLoading(false)
      })
  }

  useEffect(() => {
    loadPlan()
  }, [token])

  const handleCheckProgression = () => {
    api.checkPlanProgression(token)
      .then((response) => {
        setCheckData(response.data)
        setCheckModalOpen(true)
      })
      .catch((err) => {
        alert("Failed to check progression: " + (err.response?.data?.message || err.message))
      })
  }

  const handleProgressPlan = () => {
    setIsProgressing(true)
    api.progressChecklist(token)
      .then(() => {
        setProgressModalOpen(false)
        setIsProgressing(false)
        loadPlan() // Reload plan to show updated status
      })
      .catch((err) => {
        setIsProgressing(false)
        alert("Failed to progress plan: " + (err.response?.data?.message || err.message))
      })
  }

  const handleDownloadPdf = () => {
    downloadPdf(token, plan?.name || "plan").catch((err) => {
      alert("Failed to download PDF: " + (err.response?.data?.message || err.message))
    })
  }

  const canEditUser = (user) => {
    if (isSuperAdmin) return true
    if (isOrgAdmin || isGuideAdmin || isGuide) return user.invitation_status !== 'accepted'
    return false
  }

  const handleSaveUserProfile = async (values, userId) => {
    setEditError(null)
    try {
      await api.adminUpdateUserProfile(userId, values)
      setEditingUser(null)
      loadPlan()
    } catch (err) {
      setEditError(err.response?.data?.message || "Failed to save changes")
    }
  }

  const groupContentByKey = (content) => {
    const grouped = {}
    content.forEach((item) => {
      if (!grouped[item.key]) {
        grouped[item.key] = []
      }
      grouped[item.key].push(item.value)
    })
    return grouped
  }

  const renderContentSection = (title, keys, content) => {
    const grouped = groupContentByKey(content)
    const hasContent = keys.some(key => grouped[key] && grouped[key].length > 0)

    if (!hasContent) {
      return (
        <div className="text-gray-400 italic">No content</div>
      )
    }

    return (
      <div className="space-y-2">
        {keys.map((key) => {
          if (!grouped[key] || grouped[key].length === 0) return null
          return (
            <div key={key}>
              <div className="font-medium text-gray-700 capitalize mb-1">
                {key.replace(/_/g, ' ')}:
              </div>
              <ul className="list-disc list-inside space-y-1 ml-2">
                {grouped[key].map((value, index) => (
                  <li key={index} className="text-gray-600">
                    {value}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    )
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center flex-1">
          <div className="text-gray-600">Loading plan details...</div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center flex-1">
          <div className="text-center">
            <div className="text-red-600 mb-4">{error}</div>
            <Link to="/admin/plans">
              <Button variant="secondary">Back to Plans</Button>
            </Link>
          </div>
        </div>
      </AdminLayout>
    )
  }

  if (!plan) return null

  const allUsers = [
    ...(plan.youth || []),
    ...(plan.guide || []),
    ...(plan.mentors || []),
    ...(plan.guardians || []),
  ]

  return (
    <AdminLayout
      headerActions={
        <Link to={`/admin${userOrganization ? "/" + userOrganization?.slug : ""}/plans`}>
          <Button variant="secondary-ghost">
            <i className="fa-solid fa-arrow-left mr-2"></i>
            Back to Plans
          </Button>
        </Link>
      }
    >
      <div className="container mx-auto max-w-6xl px-4 py-8">
        {/* Plan Metadata Card */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-brandSecondary mb-2">
                {plan.name || "Untitled Plan"}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Token: {plan.token}</span>
                <span>Created: {new Date(plan.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={plan.status} />
              {plan.completed && (
                <div className="text-green-600 font-medium">✅ Completed</div>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <a
              href={`/plan/${plan.token}/activities`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button variant="secondary-outline">
                <i className="fa-solid fa-external-link mr-2"></i>
                View Plan
              </Button>
            </a>
            <Button variant="secondary-outline" onClick={handleDownloadPdf}>
              <i className="fa-solid fa-file-pdf mr-2"></i>
              Export PDF
            </Button>
          </div>
        </div>

        {/* Users & Roles Card */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-brandSecondary mb-4">
            Users & Roles
          </h2>
          {allUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Name</th>
                    <th className="text-left py-2">Email</th>
                    <th className="text-left py-2">Role</th>
                    <th className="text-left py-2">Status</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user, index) => (
                    <>
                      <tr key={index} className="border-b">
                        <td className="py-2">{user.name}</td>
                        <td className="py-2">{user.email}</td>
                        <td className="py-2 capitalize">{user.role}</td>
                        <td className="py-2">
                          <InvitationStatusBadge status={user.invitation_status} />
                        </td>
                        <td className="py-2 text-right">
                          {canEditUser(user) && editingUser?.id !== user.id && (
                            <button
                              onClick={() => { setEditingUser(user); setEditError(null) }}
                              className="text-gray-400 hover:text-brandSecondary transition-colors"
                              title="Edit user"
                            >
                              <i className="fa-solid fa-pen text-sm"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                      {editingUser?.id === user.id && (
                        <tr key={`edit-${index}`} className="border-b ">
                          <td colSpan={5} className="py-4 px-2">
                            <Formik
                              initialValues={{
                                first_name: user.first_name || user.name?.split(' ')[0] || '',
                                last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
                                email: user.email,
                              }}
                              validationSchema={editProfileSchema}
                              onSubmit={(values, actions) => {
                                handleSaveUserProfile(values, user.id).finally(() => {
                                  actions.setSubmitting(false)
                                })
                              }}
                              validateOnChange={false}
                              validateOnBlur={false}
                            >
                              {({ isSubmitting }) => (
                                <Form>
                                  <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                                    <FormikField name="first_name" title="First Name" containerClassName="mb-0">
                                      <TextField name="first_name" placeholder="First name" type="text" className="w-full" />
                                    </FormikField>
                                    <FormikField name="last_name" title="Last Name" containerClassName="mb-0">
                                      <TextField name="last_name" placeholder="Last name" type="text" className="w-full" />
                                    </FormikField>
                                    <FormikField name="email" title="Email Address" containerClassName="mb-0">
                                      <TextField name="email" placeholder="Email address" type="email" className="w-full" />
                                    </FormikField>
                                  </div>
                                  {editError && (
                                    <div className="mb-3 rounded border border-red-300 bg-red-100 p-2 text-sm text-red-700">
                                      {editError}
                                    </div>
                                  )}
                                  <div className="flex gap-2">
                                    <Button type="submit" disabled={isSubmitting}>
                                      {isSubmitting ? (
                                        <div className="flex items-center gap-2">
                                          <Spinner size="sm" />
                                          <span>Saving...</span>
                                        </div>
                                      ) : "Save"}
                                    </Button>
                                    <button
                                      type="button"
                                      onClick={() => { setEditingUser(null); setEditError(null) }}
                                      className="rounded bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </Form>
                              )}
                            </Formik>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-gray-400 italic">No users assigned</div>
          )}
        </div>

        {/* Plan Content Card */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-brandSecondary mb-4">
            Plan Content
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-brand mb-2">Identity</h3>
              {renderContentSection("Identity", ["identities"], plan.content)}
            </div>
            <div>
              <h3 className="font-bold text-gifts mb-2">Gifts</h3>
              {renderContentSection("Gifts", ["gifts"], plan.content)}
            </div>
            <div>
              <h3 className="font-bold text-vision mb-2">Vision</h3>
              {renderContentSection("Vision", ["visions"], plan.content)}
            </div>
            <div>
              <h3 className="font-bold text-circle mb-2">Mentor Circle</h3>
              {(() => {
                const newFormatMentors = plan.mentors || []
                const grouped = groupContentByKey(plan.content)
                const legacyMembers = grouped["members"] || []
                const hasAny = newFormatMentors.length > 0 || legacyMembers.length > 0

                if (!hasAny) return <div className="text-gray-400 italic">No content</div>

                return (
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    {newFormatMentors.map((mentor) => (
                      <li key={mentor.id} className="text-gray-600">{mentor.name}</li>
                    ))}
                    {legacyMembers.map((name, index) => (
                      <li key={`legacy-${index}`} className="text-gray-600">{name}</li>
                    ))}
                  </ul>
                )
              })()}
            </div>
            <div>
              <h3 className="font-bold text-encouragement mb-2">Encouragement</h3>
              {renderContentSection("Encouragement", ["encouragements"], plan.content)}
            </div>
            <div>
              <h3 className="font-bold text-commitments mb-2">Commitments</h3>
              {renderContentSection("Commitments", ["commitments"], plan.content)}
            </div>
            <div>
              <h3 className="font-bold text-blessings mb-2">Blessings</h3>
              {renderContentSection("Blessings", ["text_blessings", "audio_blessings"], plan.content)}
            </div>
          </div>
        </div>

        {/* Progression History Card */}
        <div className="bg-white border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold text-brandSecondary mb-4">
            Progression History
          </h2>
          <ProgressionTimeline history={plan.status_history} />
        </div>

        {/* Progression Controls Card */}
        <div className="bg-white border rounded-lg p-6">
          <h2 className="text-xl font-bold text-brandSecondary mb-4">
            Progression Controls
          </h2>
          <div className="flex gap-4">
            <Button onClick={handleCheckProgression} variant="secondary-outline">
              <i className="fa-solid fa-magnifying-glass mr-2"></i>
              Check Progression
            </Button>
            <Button
              onClick={() => setProgressModalOpen(true)}
              variant="default"
              disabled={!plan.can_progress}
            >
              <i className="fa-solid fa-forward mr-2"></i>
              Progress Plan
            </Button>
          </div>
          {plan.progression_blockers && plan.progression_blockers.length > 0 && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded p-3">
              <div className="font-medium text-yellow-800 mb-1">Current Blockers:</div>
              <ul className="list-disc list-inside text-sm text-yellow-700">
                {plan.progression_blockers.map((blocker, index) => (
                  <li key={index}>{blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ProgressionCheckModal
        isOpen={checkModalOpen}
        onClose={() => setCheckModalOpen(false)}
        checkData={checkData}
      />
      <ProgressPlanModal
        isOpen={progressModalOpen}
        onClose={() => setProgressModalOpen(false)}
        plan={plan}
        onConfirm={handleProgressPlan}
        isProgressing={isProgressing}
      />
    </AdminLayout>
  )
}

export default AdminPlanDetail
